import { Fragment } from 'react'
import { useFeatureFlags } from '@opallabs/api'
import { Color } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { ContextMenu, Icon, Toast, toast } from '@opallabs/lapidary'
import { AllowedEvents, TrackEvent } from '@opallabs/quanta'

/*
  Unfortunately we don't have great types yet for moments
  and content coming out of the Redux store for week view.
*/
/* eslint-disable @typescript-eslint/no-explicit-any */
type MomentContextMenuProps = {
  contextMenuCallbacks: {
    changeDaySegment: (momentId: string, newDaySegment: string | null) => void
    deleteContent: (id: string, moment: any) => void
    deleteMoment: (momentId: string, withConfirmation: boolean) => void
    duplicateContent: (
      contentItem: any,
      moment: any,
      pivotName?: string
    ) => void
    duplicateMoment: (momentId: string) => void
    renameContent: (contentId: string, newName: string) => void
    renameMoment: (momentId: string, newName: string) => void
    onAddToBoard: (args: { moment: any; defualtUnschedule: boolean }) => void
    onAddToPlan: (args: { moment: any }) => void
    pickMomentCoverAsset: (moment: any) => void
  }
  disabled: boolean
  isExpanded: boolean
  isInStories: boolean
  moment: any
  onContextMenuRename: () => void
  storyId: string
  track: TrackEvent
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export const MomentContextMenu: React.FC<
  React.PropsWithChildren<MomentContextMenuProps>
> = ({
  children,
  contextMenuCallbacks,
  disabled,
  isExpanded,
  isInStories,
  moment,
  onContextMenuRename,
  storyId,
  track,
}) => {
  const theme = useTheme()
  const { data: features } = useFeatureFlags()

  const primaryBrandSlug = moment?.brand?.slug || moment?.primaryBrand?.slug
  const iconColor = moment?.primaryStory?.color ?? Color[theme].Text.Gray
  const momentUrl = `${window.location.origin}/moments/${primaryBrandSlug}/${moment.id}`

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger disabled={disabled}>{children}</ContextMenu.Trigger>

      <ContextMenu.Portal>
        <ContextMenu.Content style={{ zIndex: 999 }}>
          <ContextMenu.Label
            css={{
              color: Color[theme].Text.Gray,
              opacity: 1,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <Icon
              ariaHidden
              glyph="moment"
              color={iconColor}
              height={16}
              useHypernova={true}
              width={16}
            />
            {moment?.title}
          </ContextMenu.Label>
          <ContextMenu.Separator />

          <ContextMenu.Item
            onSelect={() => {
              window.open(momentUrl, '_blank')
            }}
          >
            Open Moment in New Tab
          </ContextMenu.Item>

          <ContextMenu.Item
            onSelect={async () => {
              try {
                await window.navigator.clipboard.writeText(momentUrl)
                toast((props) => <Toast {...props} title="Link copied" />, {
                  type: 'success',
                })
              } catch (ex) {
                console.error(
                  'Unable to place URL into clipboard.',
                  ex && typeof ex === 'object' && 'message' in ex
                    ? ex.message
                    : ''
                )
                toast(
                  (props) => (
                    <Toast
                      {...props}
                      title="Couldn't copy the link"
                      message={momentUrl}
                    />
                  ),
                  { type: 'error' }
                )
              }
            }}
          >
            Copy Link to Moment
          </ContextMenu.Item>

          <ContextMenu.Separator />

          <ContextMenu.Item
            onSelect={() => {
              onContextMenuRename()
              track(AllowedEvents.click, {
                action: 'rename moment',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            Rename Moment
          </ContextMenu.Item>

          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.duplicateMoment(moment.id)
              const postIds = moment.posts
                ? moment.posts.map((post: any) => post.id).join(',')
                : ''
              track(AllowedEvents.click, {
                action: 'duplicate moment',
                id: moment.id,
                isExpanded,
                postIds,
              })
            }}
          >
            Duplicate Moment
          </ContextMenu.Item>

          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.pickMomentCoverAsset(moment)
              track(AllowedEvents.click, {
                action: 'change moment cover image',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            Choose Cover Image
          </ContextMenu.Item>

          <ContextMenu.Separator />
          {features?.campaign_planner ? (
            <ContextMenu.Item
              onSelect={() => {
                contextMenuCallbacks.onAddToPlan({ moment })
              }}
            >
              Add to Plan
            </ContextMenu.Item>
          ) : null}

          {features?.boards_view ? (
            <Fragment>
              <ContextMenu.Item
                onSelect={() =>
                  contextMenuCallbacks.onAddToBoard({
                    moment,
                    defualtUnschedule: false,
                  })
                }
              >
                Add to Board
              </ContextMenu.Item>
              <ContextMenu.Item
                onSelect={() =>
                  contextMenuCallbacks.onAddToBoard({
                    moment,
                    defualtUnschedule: true,
                  })
                }
              >
                Unschedule
              </ContextMenu.Item>
              <ContextMenu.Separator />
            </Fragment>
          ) : null}

          <ContextMenu.Label>Time of Day</ContextMenu.Label>

          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.changeDaySegment(moment.id, null)
              track(AllowedEvents.click, {
                action: 'change moment day segment',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            No Associated Time of Day
          </ContextMenu.Item>
          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.changeDaySegment(moment.id, 'morning')
              track(AllowedEvents.click, {
                action: 'change moment day segment',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            Morning
          </ContextMenu.Item>

          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.changeDaySegment(moment.id, 'afternoon')
              track(AllowedEvents.click, {
                action: 'change moment day segment',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            Afternoon
          </ContextMenu.Item>

          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.changeDaySegment(moment.id, 'evening')
              track(AllowedEvents.click, {
                action: 'change moment day segment',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            Evening
          </ContextMenu.Item>
          <ContextMenu.Item
            onSelect={() => {
              contextMenuCallbacks.changeDaySegment(moment.id, 'night')
              track(AllowedEvents.click, {
                action: 'change moment day segment',
                id: moment.id,
                isExpanded,
              })
            }}
          >
            Night
          </ContextMenu.Item>
          <ContextMenu.Separator />

          <ContextMenu.Item
            destructive
            onSelect={() => {
              contextMenuCallbacks.deleteMoment(moment.id, !!moment.postCount)
              track(AllowedEvents.click, {
                action: 'delete moment',
                isExpanded,
                isInStories,
                id: moment.id,
                storyId,
              })
            }}
          >
            Delete Moment
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  )
}
